import { Branch, Group, Lesson, PassCategory, Teacher } from "@/__generated__/graphql";
import { differenceInCalendarDays, isAfter, subDays } from "date-fns";
import { intersection, keyBy, map } from "lodash";
import { LessonStatus } from "../../types";

const TRIAL_BOOKING_WINDOW_DAYS = 7;
const TRIAL_BOOKING_MAX_DIFF = TRIAL_BOOKING_WINDOW_DAYS - 1;

const mapLessonStatus = (
  lesson: Lesson,
  userPasses?: PartialPass[] | null,
  options?: { isTrialOnlyUser: boolean }
): Partial<LinkedLesson> & { status: LessonStatus } => {
  const passesRecIds = map(userPasses, "recId");
  const passRecId = intersection(passesRecIds, lesson.bookingsRecIds)?.[0];
  const hasPenalty = intersection(passesRecIds, lesson.penaltiesRecIds).length > 0;
  const waitingListPassRecId = intersection(passesRecIds, lesson.waitingListRecIds)?.[0];
  const isTrialOnlyUser = options?.isTrialOnlyUser;

  const now = new Date();
  const isOutsideTrialWindow =
    !!isTrialOnlyUser &&
    !!lesson.startDateTime &&
    differenceInCalendarDays(lesson.startDateTime, now) > TRIAL_BOOKING_MAX_DIFF;
  const trialBookingOpensAt =
    isOutsideTrialWindow && lesson.startDateTime ? subDays(lesson.startDateTime, TRIAL_BOOKING_MAX_DIFF) : null;
  const bookingAllowed =
    lesson.bookingAllowed && (!isTrialOnlyUser || lesson.trialBookingAllowed) && !isOutsideTrialWindow;

  if (isAfter(lesson.startDateTime, now)) {
    if (passRecId) {
      return { status: LessonStatus.booked, passRecId, bookingAllowed, trialBookingOpensAt };
    } else if (!bookingAllowed) {
      return { status: LessonStatus.bookingNotAllowed, bookingAllowed, trialBookingOpensAt };
    } else if (waitingListPassRecId)
      return { status: LessonStatus.waiting, waitingListPassRecId, bookingAllowed, trialBookingOpensAt };
    else if (lesson.remainingSeats <= 0) return { status: LessonStatus.noSeats, bookingAllowed, trialBookingOpensAt };
    else if (lesson.remainingSeats < 3) return { status: LessonStatus.fewSeats, bookingAllowed, trialBookingOpensAt };
  } else {
    if (hasPenalty) return { status: LessonStatus.penalty, bookingAllowed, trialBookingOpensAt };
    if (lesson.passed) return { status: LessonStatus.passed, bookingAllowed, trialBookingOpensAt };
  }

  return { status: LessonStatus.none, bookingAllowed, trialBookingOpensAt };
};

export const mapLessons = (
  branches: Branch[],
  teachers: Teacher[],
  groups: Group[],
  lessons: Lesson[],
  passes?: PartialPass[] | null
): LinkedLesson[] => {
  const branchesDict = keyBy(branches, "recId");
  const teachersDict = keyBy(teachers, "recId");

  const linkedGroups = groups.map((group) => ({
    ...group,
    branch: branchesDict[String(group.branchRecId)],
    teacher: teachersDict[String(group.teacherRecId)],
  }));

  const groupsDict = keyBy(linkedGroups, "recId");

  const isTrialOnlyUser = !!passes?.length && passes.every((pass) => pass.kind?.category === PassCategory.Trial);

  return lessons.map((lesson) => ({
    ...lesson,
    ...mapLessonStatus(lesson, passes, { isTrialOnlyUser }),
    teacher: teachersDict[String(lesson.teacherRecId)],
    group: groupsDict[String(lesson.groupRecId)],
  }));
};

export const lessonHasTeacherReplacement = (lesson: LinkedLesson) =>
  lesson.teacher && lesson.group?.teacher && lesson.teacher.recId !== lesson.group?.teacher.recId;
